import {
  IsoWeekday,
  SummaryFrequency,
  SummaryMonthlyMode,
  SummaryOccurrence,
  SummarySchedule,
} from 'services/api/invoiceNotification/types';

export type IntlByKey = (
  key: string,
  params?: { [key: string]: string | number | undefined },
) => string;

// Dialog form: keeps the values of both frequencies so switching back and forth does not lose them
export type SummaryScheduleFormState = {
  frequency: SummaryFrequency;
  time: string;
  days: IsoWeekday[];
  mode: SummaryMonthlyMode;
  day: number | 'last';
  occurrence: SummaryOccurrence;
  weekday: IsoWeekday;
  timezone: string;
};

export const ALL_DAYS: IsoWeekday[] = [1, 2, 3, 4, 5, 6, 7];
export const WEEKDAYS: IsoWeekday[] = [1, 2, 3, 4, 5];
export const WEEKEND_DAYS: IsoWeekday[] = [6, 7];
export const OCCURRENCES: SummaryOccurrence[] = [1, 2, 3, 4, -1];
export const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, index) => index + 1);
export const LAST_DAY = 'last';

// Applied by the backend to settings saved before the schedule was configurable
export const LEGACY_SCHEDULE: SummarySchedule = {
  frequency: 'daily',
  time: '00:00',
  days: ALL_DAYS,
};
export const LEGACY_TIMEZONE = 'UTC';

// 30 minutes slots: 00:00, 00:30 ... 23:30
export const TIME_SLOTS = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0');
  return `${hours}:${index % 2 ? '30' : '00'}`;
});

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || LEGACY_TIMEZONE;
  } catch {
    return LEGACY_TIMEZONE;
  }
};

export const getTimezones = (): string[] => {
  // Intl.supportedValuesOf is ES2022, not in the TS lib targeted by the project
  const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  const timezones = supportedValuesOf ? supportedValuesOf('timeZone') : [];
  return Array.from(new Set([...timezones, getBrowserTimezone(), LEGACY_TIMEZONE])).sort();
};

const sameDays = (days: IsoWeekday[], expected: IsoWeekday[]) =>
  days.length === expected.length && expected.every((day) => days.includes(day));

export const isEveryDay = (days: IsoWeekday[]) => sameDays(days, ALL_DAYS);
export const isWeekdays = (days: IsoWeekday[]) => sameDays(days, WEEKDAYS);
export const isWeekend = (days: IsoWeekday[]) => sameDays(days, WEEKEND_DAYS);

// 2024-01-01 is a Monday
export const formatWeekday = (day: IsoWeekday, locale: string, format: 'short' | 'long') =>
  new Intl.DateTimeFormat(locale, { weekday: format }).format(new Date(2024, 0, day));

export const formatTime = (time: string, locale: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(
    new Date(2024, 0, 1, hours, minutes),
  );
};

export const formatTimezone = (timezone: string) => timezone.replace(/_/g, ' ');

export const formatNextRun = (nextRunAt: string, timezone: string, locale: string) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
      timeZoneName: 'short',
    }).format(new Date(nextRunAt));
  } catch {
    return new Date(nextRunAt).toLocaleString(locale);
  }
};

/**
 * Human readable schedule, e.g. "Weekdays at 7:00 AM" or "First Monday of every month at 9:00 AM"
 */
export const describeSchedule = (
  schedule: SummarySchedule,
  locale: string,
  intlByKey: IntlByKey,
): string => {
  const time = formatTime(schedule.time, locale);

  if (schedule.frequency === 'daily') {
    const days = schedule.days ?? ALL_DAYS;
    if (isEveryDay(days)) {
      return intlByKey('describe.every-day', { time });
    }
    if (isWeekdays(days)) {
      return intlByKey('describe.weekdays', { time });
    }
    if (isWeekend(days)) {
      return intlByKey('describe.weekends', { time });
    }
    const dayNames = [...days]
      .sort((a, b) => a - b)
      .map((day) => formatWeekday(day, locale, 'short'))
      .join(', ');
    return intlByKey('describe.custom-days', { days: dayNames, time });
  }

  if (schedule.mode === 'weekdayOfMonth') {
    return intlByKey('describe.weekday-of-month', {
      occurrence: intlByKey(`occurrence.${schedule.occurrence}`),
      weekday: formatWeekday(schedule.weekday ?? 1, locale, 'long'),
      time,
    });
  }

  if (schedule.day === LAST_DAY) {
    return intlByKey('describe.last-day-of-month', { time });
  }

  return intlByKey('describe.day-of-month', { day: schedule.day ?? 1, time });
};

export const toFormState = (
  schedule: SummarySchedule,
  timezone: string,
): SummaryScheduleFormState => ({
  frequency: schedule.frequency,
  time: schedule.time,
  days: schedule.days?.length ? schedule.days : ALL_DAYS,
  mode: schedule.mode ?? 'dayOfMonth',
  day: schedule.day ?? 1,
  occurrence: schedule.occurrence ?? 1,
  weekday: schedule.weekday ?? 1,
  timezone,
});

export const toSchedule = (form: SummaryScheduleFormState): SummarySchedule => {
  if (form.frequency === 'daily') {
    return {
      frequency: 'daily',
      time: form.time,
      days: [...form.days].sort((a, b) => a - b),
    };
  }
  if (form.mode === 'weekdayOfMonth') {
    return {
      frequency: 'monthly',
      time: form.time,
      mode: 'weekdayOfMonth',
      occurrence: form.occurrence,
      weekday: form.weekday,
    };
  }
  return { frequency: 'monthly', time: form.time, mode: 'dayOfMonth', day: form.day };
};
